import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/queryKeys";
import { billingService } from "@/services/billing";

export function useBillingAccountQuery() {
  return useQuery({
    queryKey: queryKeys.billingAccount(),
    queryFn: () => billingService.getAccount(),
    staleTime: 15_000,
  });
}

export function useBillingUsageMonthsQuery(months = 12) {
  return useQuery({
    queryKey: queryKeys.billingUsageMonths(months),
    queryFn: () => billingService.listUsageMonths(months),
    staleTime: 30_000,
  });
}

export function useBillingUsageDaysQuery(yearMonth: string | null) {
  return useQuery({
    queryKey: queryKeys.billingUsageDays(yearMonth ?? ""),
    queryFn: () => billingService.listUsageDays(yearMonth!),
    enabled: Boolean(yearMonth),
    staleTime: 30_000,
  });
}

export function useBillingUsageEventsQuery(date: string | null) {
  return useQuery({
    queryKey: queryKeys.billingUsageEvents(date ?? ""),
    // Max page the API allows; the UI shows a "showing N of M" note past that.
    queryFn: () => billingService.listUsageEvents(date!, { page_size: 100 }),
    enabled: Boolean(date),
    staleTime: 15_000,
  });
}

export function useBillingInvoicesQuery(page = 1) {
  return useQuery({
    queryKey: queryKeys.billingInvoices(page),
    // Max page the API allows; the UI shows a "showing N of M" note past that.
    queryFn: () => billingService.listInvoices({ page, page_size: 100 }),
    staleTime: 15_000,
  });
}

export function useAttachPaymentMethodMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: string) => billingService.attachPaymentMethod(paymentMethodId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billingAccount() });
      void qc.invalidateQueries({ queryKey: queryKeys.billingInvoicesAll() });
    },
  });
}

export function useRemovePaymentMethodMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => billingService.removePaymentMethod(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billingAccount() });
    },
  });
}

export function useRetryInvoiceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: number) => billingService.retryInvoice(invoiceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billingInvoicesAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.billingAccount() });
    },
  });
}
